'use client';

import { useEffect } from 'react';
import { useSetAtom } from 'jotai';
import { usePathname } from 'next/navigation';
import { PageType } from '@/types/pageType';
import { pageTypeOverrideAtom } from '@/atoms/pageTypeAtom';

/**
 * Lets a page declare its page type for the shared page shell (header
 * navigation state, promo popup) when it cannot be derived from the pathname,
 * or hide the promo banner for the current URL. Renders nothing, so it can sit
 * anywhere in the page.
 *
 * The override is keyed by the URL it was set for so it stops applying as soon
 * as the router moves on, rather than when this component's cleanup runs — see
 * `pageTypeOverrideAtom`. This needs the real browser pathname, not the
 * unlocalized route from `@/i18n/navigation`, which is the same
 * (`/[[...slug]]`) for every CMS page.
 */
export const PageTypeSetter = ({
  pageType,
  hidePromoBanner,
}: {
  pageType?: PageType;
  hidePromoBanner?: boolean;
}) => {
  const pathname = usePathname();
  const setPageTypeOverride = useSetAtom(pageTypeOverrideAtom);

  useEffect(() => {
    setPageTypeOverride({ pathname, pageType, hidePromoBanner });
    return () => setPageTypeOverride(null);
  }, [pathname, pageType, hidePromoBanner, setPageTypeOverride]);

  return null;
};
